import React, { useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import RingSpinner from 'components/Spinner/Ring';
import Blocker from 'components/Blocker';
import Modal from 'components/Modal';
import Media from 'components/Media';
import './media-viewer.scss';

const minSwipeDistance = 50;

const MediaViewer = ({ show, fileIndex, files, togglePreview }) => {
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [isAnimating, setIsAnimating] = useState(false);
    const [nextIndex, setNextIndex] = useState(-1);
    const [animationDirection, setAnimationDirection] = useState('');
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const [animationTimer, setAnimationTimer] = useState(null);

    const handleClose = () => {
        if (animationTimer) {
            clearTimeout(animationTimer);
            setAnimationTimer(null);
        }

        setCurrentIndex(-1);
        setIsAnimating(false);
        setNextIndex(-1);
        setAnimationDirection('');
        setTouchStart(null);
        setTouchEnd(null);
        togglePreview();
    };

    const hasMultipleAttachments = files && files.length > 1;

    useEffect(() => {
        if (show && fileIndex >= 0) {
            setCurrentIndex(fileIndex);
        }
    }, [fileIndex, show]);

    useEffect(() => {
        return () => {
            if (animationTimer) {
                clearTimeout(animationTimer);
            }
        };
    }, [animationTimer]);

    const goToPrevious = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (isAnimating) return;
        const prevIndexValue = currentIndex < files.length - 1 ? currentIndex + 1 : 0;

        setCurrentIndex(prevIndexValue);
        setNextIndex(-1);
        setAnimationDirection('slide-left');
        setIsAnimating(true);

        if (animationTimer) {
            clearTimeout(animationTimer);
        }

        const timer = setTimeout(() => {
            setAnimationDirection('');
            setIsAnimating(false);
            setAnimationTimer(null);
        }, 300);

        setAnimationTimer(timer);
    };

    const goToNext = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (isAnimating) return;

        const nextIndexValue = currentIndex > 0 ? currentIndex - 1 : files.length - 1;

        setCurrentIndex(nextIndexValue);
        setNextIndex(-1);
        setAnimationDirection('slide-right');
        setIsAnimating(true);

        if (animationTimer) {
            clearTimeout(animationTimer);
        }

        const timer = setTimeout(() => {
            setAnimationDirection('');
            setIsAnimating(false);
            setAnimationTimer(null);
        }, 300);

        setAnimationTimer(timer);
    };

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (files.length > 1 && !isAnimating) {
            if (isLeftSwipe) {
                goToPrevious();
            } else if (isRightSwipe) {
                goToNext();
            }
        }
    };

    const currentFile = files[currentIndex];
    const nextFile = files[nextIndex];

    return (
        <>
            { show && currentFile && (
                <Modal
                    className="preview-modal gallery-modal"
                    title={ I18n.t('preview')}
                    onClose={ handleClose }
                >
                    <div className={`gallery-container ${animationDirection}`}>
                        {hasMultipleAttachments && (
                            <button
                                type="button"
                                className="gallery-nav gallery-nav-left"
                                onClick={goToNext}
                                disabled={isAnimating}
                            />
                        )}

                        <div
                            className="preview-wrapper"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            {
                                isAnimating && (
                                    <Blocker className="blocker-container">
                                        <RingSpinner />
                                    </Blocker>
                                )
                            }

                            <div className="preview current-slide">
                                <Media
                                    key={`current-${currentIndex}`}
                                    controls
                                    src={
                                        currentFile.category === 'image'
                                            ? (currentFile.thumbnailUrl || currentFile.previewUrl || currentFile.url)
                                            : (currentFile.previewUrl || currentFile.url || currentFile.thumbnailUrl)
                                    }
                                    alt=""
                                    mediaType={ currentFile?.type ? currentFile.type : undefined }
                                />
                            </div>

                            {nextFile && (
                                <div className="preview next-slide">
                                    <Media
                                        key={`next-${nextIndex}`}
                                        controls
                                        src={
                                            nextFile.category === 'image'
                                                ? (nextFile.thumbnailUrl || nextFile.previewUrl || nextFile.url)
                                                : (nextFile.previewUrl || nextFile.url || nextFile.thumbnailUrl)
                                        }
                                        mediaType={ nextFile?.type ? nextFile.type : undefined }
                                    />
                                </div>
                            )}
                        </div>

                        {hasMultipleAttachments && (
                            <button
                                type="button"
                                className="gallery-nav gallery-nav-right"
                                onClick={goToPrevious}
                                disabled={isAnimating}
                            />
                        )}
                    </div>
                </Modal>
            ) }
        </>
    )
}

export default MediaViewer
